// How many allowance "blocks" a membership is worth.
//
// A membership's allowance is stored as credits.totalCredits (blocks) x credits.perPeriod
// (sessions in one block). Under the old model a CUSTOM pack's durationValue WAS the block —
// "3 weeks, 2 classes in it" — so one booking bought exactly one block and totalCredits = 1 was
// right.
//
// sessionLimitUnit broke that equivalence. "2 a week, running for 3 months" has a cap whose
// period (a week) is no longer the plan's span (3 months), so one block is one WEEK and the
// booking is worth about thirteen of them. Still sending 1 made the whole membership two
// sessions long: book two and it reported "2/2 booked - 0 remaining" and ended, months early.
//
// Counting is calendar-snapped, not span/length: "2 a week" means the weeks a family recognises,
// and it matches how the backend bills (helpers/allowanceWindow.ts windowsInCycle).
import { getInstance, getInstanceClone } from "./timeutil";

const UNIT: Record<string, "day" | "week" | "month"> = { DAY: "day", WEEK: "week", MONTH: "month" };

/**
 * The period a pack's session limit counts over: "CYCLE" (billing period), "PACK", "DAY", "WEEK",
 * "MONTH", or null where the pack has no say (the caller's legacy windows apply).
 *
 * Packs are now always saved with a unit. One saved without (before that) is read the way it was
 * saved, as the backend does (functions/src/helpers/allowanceWindow.ts effectiveUnit): production's
 * prepaid recurring CUSTOM pack put "No of classes" next to how long the pack runs, so its count is
 * for the whole pack; postpaid packs only ever meant "per billing period".
 */
export function sessionLimitUnitOf(pricingPack: any): string | null {
    const stored = pricingPack?.sessionLimitUnit;
    if (stored) return String(stored).toUpperCase();
    if (pricingPack?.paymentType === "RECURRING" && pricingPack?.frequency === "CUSTOM") {
        return pricingPack?.paymentTiming === "AFTER" ? "CYCLE" : "PACK";
    }
    return null;
}

/**
 * Allowance windows between two instants, counting any window the range touches.
 * Returns 1 for an unrecognised unit or a non-positive range, so a caller can always use it.
 */
export function countAllowanceWindows(startMs: number, endMs: number, sessionLimitUnit?: string | null): number {
    const unit = UNIT[String(sessionLimitUnit || "").toUpperCase()];
    if (!unit || !(startMs > 0) || !(endMs > startMs)) return 1;

    let cursor = getInstanceClone(getInstance(startMs)).startOf(unit === "week" ? "isoWeek" : unit);
    let count = 0;
    // Bounded: the smallest window is a day and no plan sensibly runs past a few years.
    for (let guard = 0; guard < 1200; guard++) {
        if (cursor.valueOf() >= endMs) break;
        count += 1;
        cursor = getInstanceClone(cursor).add(1, unit);
    }
    return Math.max(1, count);
}

/**
 * credits.totalCredits for a booking: the number of blocks its span is worth.
 * Packs that name no allowance unit keep the previous value untouched.
 */
export function allowanceBlocksForBooking(
    pricingPack: any,
    startMs: number,
    endMs: number,
    fallback: number
): number {
    const unit = sessionLimitUnitOf(pricingPack);
    if (pricingPack?.unlimitedClasses || !(Number(pricingPack?.noOfClasses) > 0)) return fallback;
    // A recurring CUSTOM pack's per-billing-period limit makes the membership worth one block per
    // billing period it spans (the windows the pickers and the backend count). Every other pack
    // without a unit keeps the previous value.
    if (!unit || unit === "CYCLE") {
        const recurringCustom = pricingPack?.paymentType === "RECURRING" && pricingPack?.frequency === "CUSTOM";
        return recurringCustom ? countBillingPeriods(startMs, endMs, pricingPack) : fallback;
    }
    if (unit === "PACK") return 1;   // one allowance for the whole pack
    return countAllowanceWindows(startMs, endMs, unit);
}

/**
 * Billing periods ("bill every N units", walked from the start) that a range touches.
 *
 * A membership ends at the close of the same date N months on ("3 Nov + 3 months = 3 Feb"), so
 * a period starting on the end's own day would be one day long and not a billing period at all.
 */
export function countBillingPeriods(startMs: number, endMs: number, pricingPack: any): number {
    if (!(startMs > 0) || !(endMs > startMs)) return 1;
    const lastDayStart = getInstanceClone(getInstance(endMs)).startOf("day").valueOf();
    const value = Number(pricingPack?.billEveryValue ?? pricingPack?.durationValue) || 1;
    const raw = String(pricingPack?.billEveryUnit || pricingPack?.durationUnit || "MONTHS").toUpperCase();
    const step = raw === "DAYS" ? "day" : raw === "WEEKS" ? "week" : "month";
    let cursor = getInstanceClone(getInstance(startMs)).startOf("day");
    let count = 0;
    for (let guard = 0; guard < 1200; guard++) {
        if (cursor.valueOf() >= endMs || (count > 0 && cursor.valueOf() >= lastDayStart)) break;
        count += 1;
        cursor = getInstanceClone(cursor).add(value, step);
    }
    return Math.max(1, count);
}
